/**
 * Contractor Company Portal - Login Page
 * 
 * Company-level portal for contractor helpdesk/office users.
 * Separate from magic-link engineer flow.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Building2, Lock, Mail, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ContractorCompanyLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/api/contractor-company-auth/login`, {
        email,
        password,
      });

      if (res.data.success && res.data.token) {
        // Store token and contractor info
        localStorage.setItem('contractor_portal_token', res.data.token);
        localStorage.setItem('contractor_portal_id', res.data.contractor_id);
        localStorage.setItem('contractor_portal_name', res.data.contractor_name);
        
        navigate('/contractor-company/dashboard');
      } else {
        setError('Login failed. Please check your credentials.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md border-slate-200 bg-white shadow-xl">
        <CardHeader className="text-center">
          <img 
            src="/phs-logo.png" 
            alt="Park Home Support" 
            className="h-24 mx-auto mb-4"
          />
          <CardTitle className="text-2xl font-bold text-slate-800">Contractor Portal</CardTitle>
          <CardDescription className="text-slate-500">
            Sign in to access your company dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-white border-slate-300 text-slate-800 placeholder:text-slate-400"
                  required
                  data-testid="contractor-portal-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-white border-slate-300 text-slate-800 placeholder:text-slate-400"
                  required
                  data-testid="contractor-portal-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#1F7A78] hover:bg-[#16615f] text-white"
              disabled={loading}
              data-testid="contractor-portal-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            <p>Need access? Contact your system administrator</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
